import React, { Component, Fragment } from 'react';
import { Button, Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { uniqueId } from 'lodash';

import OrderLink from '../OrderLink';
import globalTranslations from '../../../i18n/globalTranslations';
import generatePath from '../../../util/generatePath';
import Paths from '../../../types/Paths';
import OrderTotalClass from '../../../types/OrderTotalClass';
import RouteService from '../../../services/RouteService';
import ProductTypeClass from '../../../types/ProductTypeClass';
import ProductImage from '../../product/ProductImage';
import ProductImageModel from '../../../models/ProductImage';
import { modelOf } from '../../../prop-types';
import Order from '../../../models/Order';
import OrderStatusLabel from '../OrderStatusLabel';
import Price from '../../product/Price';
import AccountStore from '../../../store/AccountStore';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import CartStore from '../../../store/CartStore';
import RequestState from '../../../types/RequestState';
import OrderCloneButton from '../OrderCloneButton';

const MAX_PRODUCT_IMAGE_COUNT = 6;

@observer
export class OrderPreview extends Component {
  getTotalPrice = () => {
    const { order } = this.props;
    const totalPrice = order.totals.find(
      (total) => total.class === OrderTotalClass.TOTAL
    );

    return totalPrice.value || null;
  };

  getTotalTaxlessPrice = () => {
    const { order } = this.props;
    const totalPrice = order.totals.find(
      (total) => total.class === OrderTotalClass.TAXLESS_TOTAL
    );

    return totalPrice.value || null;
  };

  getOrderStatus = () => {
    const { order } = this.props;

    if (!order.last_status) {
      return null;
    }

    return <OrderStatusLabel status={order.last_status} />;
  };

  getProductCount = () => {
    const physicalProducts = this.getPhysicalProducts();
    if (!physicalProducts) {
      return null;
    }

    return (
      <FormattedMessage
        {...globalTranslations.productCount}
        values={{ count: physicalProducts.length }}
      />
    );
  };

  getProductReference = (order) => {
    const reference = order.getOrderReference();
    return (
      order && (
        <Fragment>
          {reference && (
            <FormattedMessage {...globalTranslations.yourReferenceTitle} />
          )}
          {reference && ':'}
          {reference}
        </Fragment>
      )
    );
  };

  getPhysicalProducts = () => {
    return this.props.order.products.filter(
      (orderProduct) => orderProduct.type === ProductTypeClass.PHYSICAL
    );
  };

  getProductImages = () => {
    const physicalProducts = this.getPhysicalProducts();

    if (!physicalProducts || physicalProducts.length === 0) {
      return null;
    }

    return physicalProducts
      .filter((orderProduct, index) => index <= MAX_PRODUCT_IMAGE_COUNT)
      .map((orderProduct, index) => {
        const productImageModel = orderProduct.image_url
          ? ProductImageModel.create({
              id: index,
              product_id: orderProduct.id,
              sizes: {
                small: orderProduct.image_url
              },
              description: ''
            })
          : null;

        const productImage = (
          <ProductImage
            product={orderProduct}
            productImage={productImageModel}
            size="small"
          />
        );

        const product = orderProduct.product_url ? (
          <MaybeExternalLink to={orderProduct.product_url}>
            {productImage}
          </MaybeExternalLink>
        ) : (
          productImage
        );

        return (
          <div
            key={uniqueId(orderProduct.id)}
            className="OrderPreview__products-image"
          >
            {product}
          </div>
        );
      });
  };

  getPath = () => {
    const { order, routeService } = this.props;
    return routeService.getPath(
      generatePath(Paths.MyAccountOrder, { id: order.id })
    );
  };

  getOrderViewButton = () => {
    return (
      <Button
        className="OrderPreview__button-view"
        color="primary"
        tag={Link}
        to={this.getPath()}
      >
        <FormattedMessage {...globalTranslations.toOrder} />
      </Button>
    );
  };

  getOrderDate = () => {
    const { order } = this.props;
    return DateTime.fromISO(order.date_purchased).toLocaleString();
  };

  render() {
    const { order, accountStore, cartStore } = this.props;
    if (!order) {
      return null;
    }

    return (
      <div className="OrderPreview">
        <Row className="OrderPreview__row">
          <Col md="4" sm="12">
            <div className="OrderPreview__order-information">
              <div className="OrderPreview__link">
                <OrderLink
                  id={order.id}
                  link={this.getPath()}
                  date_purchased={order.date_purchased}
                />
              </div>
              <div className="OrderPreview__products-images">
                {this.getProductImages()}
              </div>
              <div className="OrderPreview__count">
                {this.getProductCount()}
              </div>
              <div className="OrderPreview__order-reference">
                {this.getProductReference(order)}
              </div>
            </div>
          </Col>
          <Col md="2" sm="12">
            <div className="OrderPreview__date">{this.getOrderDate()}</div>
          </Col>
          <Col md="2" sm="12">
            <div className="OrderPreview__status">{this.getOrderStatus()}</div>
          </Col>
          <Col md="2" sm="12">
            <div className="OrderPreview__price">
              <Price price={this.getTotalPrice()} />
            </div>
            {accountStore.isRetailer && (
              <div className="OrderPreview__price">
                <Price price={this.getTotalTaxlessPrice()} />{' '}
                <FormattedMessage {...globalTranslations.taxExcluded} />
              </div>
            )}
          </Col>
          <Col md="2" sm="12">
            <div className="OrderPreview__button">
              {this.getOrderViewButton()}
              <OrderCloneButton
                order={order}
                disabled={!cartStore.state === RequestState.LOADING}
              />
            </div>
          </Col>
        </Row>
      </div>
    );
  }
}

OrderPreview.propTypes = {
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  order: modelOf(Order),
  intl: intlShape.isRequired
};

export default inject('routeService', 'accountStore', 'cartStore')(
  injectIntl(OrderPreview)
);



// WEBPACK FOOTER //
// ./src/components/order/OrderPreview/index.jsx